import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { createSolicitud } from '@/services/solicitudesDesarrollo.service';
import { fetchUsers } from '@/services/users.service';
import { uploadDocumentFlow } from '@/services/documentsService';

const SolicitudFormModal = ({ open, onClose, onSuccess, user }) => {
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    tipo: 'tarea',
    prioridad: 'media',
    modulo: '',
    asignadoA: null,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await fetchUsers({ limit: 100, offset: 1 });
      const usersList = response?.data || response?.items || response || [];
      setUsers(Array.isArray(usersList) ? usersList : []);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      toast({
        title: 'Advertencia',
        description: 'Solo se permiten archivos de imagen',
        variant: 'destructive',
      });
    }
    
    setSelectedImages(prev => [...prev, ...imageFiles]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.titulo.trim()) {
      newErrors.titulo = 'El título es requerido';
    }
    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      
      // Upload images first
      let uploadedImageUrls = [];
      if (selectedImages.length > 0) {
        setUploadingImages(true);
        try {
          const uploadPromises = selectedImages.map(file => 
            uploadDocumentFlow({
              ownerType: 'solicitud',
              ownerId: 'temp_' + Date.now(),
              docType: 'imagen',
              file,
            })
          );
          const uploadResults = await Promise.all(uploadPromises);
          uploadedImageUrls = uploadResults.map(result => result.id || result._id);
        } catch (uploadError) {
          console.error('Error uploading images:', uploadError);
          toast({
            title: 'Advertencia',
            description: 'Algunas imágenes no se pudieron subir',
            variant: 'destructive',
          });
        } finally {
          setUploadingImages(false);
        }
      }
      
      const solicitudData = {
        ...formData,
        creadoPor: {
          id: user.id,
          nombre: user.name,
          email: user.email,
        },
        adjuntos: uploadedImageUrls,
      };

      // Remove asignadoA if null
      if (!solicitudData.asignadoA) {
        delete solicitudData.asignadoA;
      }

      await createSolicitud(solicitudData);
      
      setFormData({
        titulo: '',
        descripcion: '',
        tipo: 'tarea',
        prioridad: 'media',
        modulo: '',
        asignadoA: null,
      });
      setSelectedImages([]);
      
      onSuccess();
    } catch (error) {
      console.error('Error creating solicitud:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear la solicitud',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nueva Solicitud</DialogTitle>
          <DialogDescription>
            Crea una nueva solicitud de desarrollo, bug o tarea
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="titulo">Título *</Label>
            <Input
              id="titulo"
              value={formData.titulo}
              onChange={(e) => handleChange('titulo', e.target.value)}
              placeholder="Título descriptivo de la solicitud"
              className={errors.titulo ? 'border-red-500' : ''}
            />
            {errors.titulo && (
              <p className="text-sm text-red-500">{errors.titulo}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción *</Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => handleChange('descripcion', e.target.value)}
              placeholder="Describe detalladamente la solicitud..."
              rows={4}
              className={errors.descripcion ? 'border-red-500' : ''}
            />
            {errors.descripcion && (
              <p className="text-sm text-red-500">{errors.descripcion}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo *</Label>
              <Select
                value={formData.tipo}
                onValueChange={(value) => handleChange('tipo', value)}
              >
                <SelectTrigger id="tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="error">🚨  Error</SelectItem>
                  <SelectItem value="sugerencia">✨ Sugerencia</SelectItem>
                  <SelectItem value="tarea">📋 Tarea</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prioridad">Prioridad *</Label>
              <Select
                value={formData.prioridad}
                onValueChange={(value) => handleChange('prioridad', value)}
              >
                <SelectTrigger id="prioridad">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critica">🔴 Crítica</SelectItem>
                  <SelectItem value="alta">🟠 Alta</SelectItem>
                  <SelectItem value="media">🟡 Media</SelectItem>
                  <SelectItem value="baja">🟢 Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="modulo">Módulo (opcional)</Label>
            <Input
              id="modulo"
              value={formData.modulo}
              onChange={(e) => handleChange('modulo', e.target.value)}
              placeholder="Ej: Inventario, Solicitudes, Dashboard..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="asignadoA">Asignar a (opcional)</Label>
            <Select
              value={formData.asignadoA?.id || "unassigned"}
              onValueChange={(value) => {
                if (value === "unassigned") {
                  handleChange('asignadoA', null);
                } else {
                  const selectedUser = users.find(u => u._id === value);
                  if (selectedUser) {
                    handleChange('asignadoA', {
                      id: selectedUser._id,
                      nombre: selectedUser.nombre,
                      email: selectedUser.correo,
                    });
                  }
                }
              }}
            >
              <SelectTrigger id="asignadoA">
                <SelectValue placeholder={loadingUsers ? "Cargando usuarios..." : "Seleccionar usuario"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Sin asignar</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u._id} value={u._id}>
                    {u.nombre} ({u.correo})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Imágenes adjuntas (opcional)</Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  id="image-upload"
                  disabled={loading || uploadingImages}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('image-upload').click()}
                  disabled={loading || uploadingImages}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Seleccionar imágenes
                </Button>
              </div>
              
              {selectedImages.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {selectedImages.map((file, index) => (
                    <div key={index} className="relative border rounded-lg p-2 flex items-center gap-2">
                      <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate flex-1">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveImage(index)}
                        disabled={loading || uploadingImages}
                        className="h-6 w-6"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || uploadingImages}>
              {uploadingImages ? 'Subiendo imágenes...' : loading ? 'Creando...' : 'Crear Solicitud'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SolicitudFormModal;
